import { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Group, Modal, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { LookupPlan } from '../Enrich/output';

interface Props {
  opened: boolean;
  plan: LookupPlan | null;
  onConfirm: () => void;
  onBack: () => void;
}

/** Risk warning before Source 3. Continue stays disabled until the box is ticked. */
export function MemberLookupWarningModal({ opened, plan, onConfirm, onBack }: Props) {
  const [understood, setUnderstood] = useState(false);
  useEffect(() => { if (opened) setUnderstood(false); }, [opened]);

  return (
    <Modal
      opened={opened}
      onClose={onBack}
      title="Look up members?"
      size="md"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="This may attach the wrong person's details">
          <Text size="sm">
            These details were not found in the requests, so the member records are used instead. A gap is
            only filled when exactly one member has a first name and surname that match exactly, but two
            different people can share a name, and a member record may be out of date or belong to someone
            else. Review every filled value before you use the file. Values filled this way are marked
            "from source 3" in the Filled from column.
          </Text>
        </Alert>

        {plan && (
          <Text size="sm" c="dimmed">
            {plan.people} {plan.people === 1 ? 'person' : 'people'} to look up:{' '}
            <b>{plan.withMemberID}</b> by member id (safest), <b>{plan.byName}</b> by name match
            {plan.noRoute > 0 && <>, <b>{plan.noRoute}</b> cannot be looked up (no member id and no full name)</>}.
          </Text>
        )}

        <Checkbox
          checked={understood}
          onChange={(e) => setUnderstood(e.currentTarget.checked)}
          label="I understand and want to continue"
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onBack}>Go back</Button>
          <Button disabled={!understood} onClick={onConfirm}>Continue</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
